// src/pages/TermsOfService.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDarkMode } from "../context/DarkModeContext";

const TermsOfService: React.FC = () => {
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-gray-100'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>Terms of Service | FindWork Kenya</title>
        <meta
          name="description"
          content="Read FindWork's terms of service. Understand your rights and responsibilities when using our job board platform in Kenya."
        />
        <link rel="canonical" href="https://findwork.ltd/terms-of-service" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r ${
              isDark
                ? 'from-blue-400 to-blue-600 text-white'
                : 'from-sky-500 to-blue-600 text-transparent bg-clip-text'
            }`}
          >
            Terms of Service
          </h1>
          <p
            className={`text-lg ${isDark ? 'text-gray-400' : 'text-blue-700'}`}
          >
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content Container */}
        <div
          className={`rounded-xl shadow-lg p-6 sm:p-8 ${
            isDark
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-blue-200 shadow-blue-100'
          }`}
        >
          {/* Introduction */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              1. Introduction
            </h2>
            <p
              className={`mb-4 leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              Welcome to FindWork ("we," "our," or "us"). These Terms of Service
              ("Terms") govern your access to and use of the FindWork platform,
              including our website, mobile applications, and services
              (collectively, the "Platform"). Please read these Terms carefully
              before using our Platform.
            </p>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              By accessing or using our Platform, you agree to be bound by these
              Terms and our Privacy Policy. If you disagree with any part of
              these Terms, you may not access our Platform.
            </p>
          </section>

          {/* Account Registration */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              2. Account Registration
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Eligibility:
                </strong>{' '}
                You must be at least 18 years old to use our Platform. By
                registering, you represent and warrant that you meet this age
                requirement.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Account Security:
                </strong>{' '}
                You are responsible for maintaining the confidentiality of your
                account credentials and for all activities that occur under your
                account.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Accurate Information:
                </strong>{' '}
                You agree to provide accurate, current, and complete information
                during registration and to update such information to keep it
                accurate.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  One Account:
                </strong>{' '}
                Each user may maintain only one account. Multiple accounts are
                prohibited without explicit written permission.
              </p>
            </div>
          </section>

          {/* User Responsibilities */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              3. User Responsibilities
            </h2>
            <div
              className={`space-y-4 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  Job Seekers
                </h3>
                <ul
                  className={`list-disc list-inside space-y-2 ml-4 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  <li>
                    Provide accurate and truthful information in your profile
                    and applications
                  </li>
                  <li>
                    Do not misrepresent your qualifications, experience, or
                    education
                  </li>
                  <li>
                    Respect employer confidentiality and intellectual property
                  </li>
                  <li>Do not spam employers with irrelevant applications</li>
                  <li>Maintain professional conduct in all communications</li>
                </ul>
              </div>

              <div>
                <h3
                  className={`text-lg font-medium mb-2 ${
                    isDark ? 'text-gray-200' : 'text-blue-700'
                  }`}
                >
                  Employers
                </h3>
                <ul
                  className={`list-disc list-inside space-y-2 ml-4 ${
                    isDark ? 'text-gray-400' : 'text-blue-800'
                  }`}
                >
                  <li>
                    Post legitimate job opportunities with accurate descriptions
                  </li>
                  <li>
                    Do not discriminate based on protected characteristics
                  </li>
                  <li>Respond to applications in a timely manner</li>
                  <li>Maintain confidentiality of applicant information</li>
                  <li>Comply with all applicable employment laws</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Prohibited Activities */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              4. Prohibited Activities
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                You agree not to engage in any of the following prohibited
                activities:
              </p>
              <ul
                className={`list-disc list-inside space-y-2 ml-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-800'
                }`}
              >
                <li>Posting false, misleading, or fraudulent information</li>
                <li>
                  Harassing, threatening, or discriminating against other users
                </li>
                <li>
                  Using the Platform for any illegal or unauthorized purpose
                </li>
                <li>
                  Attempting to gain unauthorized access to other accounts or
                  systems
                </li>
                <li>
                  Scraping, data mining, or extracting user information without
                  permission
                </li>
                <li>Uploading viruses, malware, or other malicious code</li>
                <li>Circumventing any security or access controls</li>
                <li>Impersonating any person or entity</li>
                <li>Violating any applicable laws or regulations</li>
              </ul>
            </div>
          </section>

          {/* Subscription and Payments */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              5. Subscription and Payments
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Subscription Plans:
                </strong>{' '}
                We offer various subscription plans with different features and
                pricing. All fees are stated in the applicable currency and are
                exclusive of taxes.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Billing Cycle:
                </strong>{' '}
                Subscription fees are billed in advance on a monthly or annual
                basis, depending on your chosen plan.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Auto-Renewal:
                </strong>{' '}
                Subscriptions automatically renew at the end of each billing
                period unless canceled before the renewal date.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Cancellation:
                </strong>{' '}
                You may cancel your subscription at any time, but no refunds
                will be provided for partial billing periods.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Price Changes:
                </strong>{' '}
                We reserve the right to change subscription fees with 30 days'
                notice to current subscribers.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              6. Intellectual Property
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Platform Content:
                </strong>{' '}
                All content on the Platform, including text, graphics, logos,
                and software, is our property or licensed to us and protected by
                intellectual property laws.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  User Content:
                </strong>{' '}
                You retain ownership of the content you submit to the Platform,
                but grant us a worldwide, non-exclusive license to use, display,
                and distribute your content in connection with the Platform.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Trademarks:
                </strong>{' '}
                FindWork names, logos, and related marks are our trademarks. You
                may not use them without our prior written permission.
              </p>
            </div>
          </section>

          {/* Privacy */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              7. Privacy
            </h2>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              Your privacy is important to us. Our{' '}
              <button
                onClick={() => handleNavigation('/privacy-policy')}
                className={`font-medium underline ${
                  isDark
                    ? 'text-blue-400 hover:text-blue-300'
                    : 'text-blue-600 hover:text-blue-800'
                }`}
              >
                Privacy Policy
              </button>{' '}
              explains how we collect, use, and protect your personal
              information. By using our Platform, you consent to our collection
              and use of your data as described in the Privacy Policy.
            </p>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              8. Termination
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  By You:
                </strong>{' '}
                You may terminate your account at any time by contacting us or
                using the account deletion feature in your settings.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  By Us:
                </strong>{' '}
                We may suspend or terminate your account if we believe you have
                violated these Terms or applicable laws.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Effect of Termination:
                </strong>{' '}
                Upon termination, your right to use the Platform will
                immediately cease. Some provisions of these Terms will survive
                termination.
              </p>
            </div>
          </section>

          {/* Disclaimers and Limitation of Liability */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              9. Disclaimers and Limitation of Liability
            </h2>
            <div
              className={`space-y-3 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  No Guarantee:
                </strong>{' '}
                We do not guarantee that you will find employment or suitable
                candidates through our Platform. We are not responsible for
                employment decisions made by users.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Platform "As Is":
                </strong>{' '}
                The Platform is provided "as is" without warranties of any kind,
                either express or implied.
              </p>
              <p>
                <strong className={isDark ? 'text-gray-200' : 'text-blue-800'}>
                  Limitation of Liability:
                </strong>{' '}
                To the maximum extent permitted by law, we shall not be liable
                for any indirect, incidental, special, or consequential damages
                arising from your use of the Platform.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              10. Changes to Terms
            </h2>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              We may modify these Terms at any time. We will notify you of
              material changes through the Platform or via email. Your continued
              use of the Platform after such changes constitutes acceptance of
              the modified Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              11. Governing Law
            </h2>
            <p
              className={`leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              These Terms shall be governed by and construed in accordance with
              the laws of [Your Country/State], without regard to its conflict
              of law provisions.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2
              className={`text-2xl font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              12. Contact Information
            </h2>
            <div
              className={`space-y-2 ${
                isDark ? 'text-gray-300' : 'text-blue-900'
              }`}
            >
              <p>
                If you have any questions about these Terms, please contact us:
              </p>
              <p>Email: legal@FindWork.com</p>
              <p>Address: [Your Company Address]</p>
              <p>Phone: [Your Contact Number]</p>
            </div>
          </section>

          {/* Acceptance */}
          <section
            className={`p-4 rounded-lg ${
              isDark
                ? 'bg-gray-700 border border-gray-600'
                : 'bg-gradient-to-r from-sky-100 to-blue-100 border border-blue-300'
            }`}
          >
            <p
              className={`text-center font-medium ${
                isDark ? 'text-gray-200' : 'text-blue-800'
              }`}
            >
              By using FindWork, you acknowledge that you have read, understood,
              and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div
          className={`mt-8 text-center ${
            isDark ? 'text-gray-400' : 'text-blue-700'
          }`}
        >
          <p className="text-sm">
            Need help?{' '}
            <button
              onClick={() => handleNavigation('/contact-support')}
              className={`font-medium underline ${
                isDark
                  ? 'text-blue-400 hover:text-blue-300'
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              Contact Support
            </button>{' '}
            or{' '}
            <button
              onClick={() => handleNavigation('/privacy-policy')}
              className={`font-medium underline ${
                isDark
                  ? 'text-blue-400 hover:text-blue-300'
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              View Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
